/**
 * GenericDateTime
 * A lightweight, fluent wrapper for native JavaScript Date objects with enhanced time support.
 * Designed to provide Moment-like functionality without external dependencies.
 */
export class GenericDateTime {
  static Units = {
    DAY: 'day',
    MONTH: 'month',
    YEAR: 'year',
    HOUR: 'hour',
    MINUTE: 'minute',
    SECOND: 'second',
    MS: 'ms',
  };

  static Defaults = {
    FORMAT: { year: 'numeric', month: '2-digit', day: '2-digit' },
    DATETIME_FORMAT: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    },
    TIME_FORMAT: { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
  };

  constructor(date) {
    this._date = this._parse(date);
    this._locale = undefined;
    this._format = GenericDateTime.Defaults.FORMAT;
  }

  static from(date) {
    return new GenericDateTime(date);
  }

  static now() {
    return new GenericDateTime(new Date());
  }

  static parse(date) {
    return new GenericDateTime(date);
  }

  _parse(date) {
    if (date instanceof GenericDateTime) return new Date(date.toDate().getTime());
    if (date instanceof Date) return new Date(date.getTime());
    if (typeof date === 'string' || typeof date === 'number') {
      const d = new Date(date);
      return Number.isNaN(d.getTime()) ? new Date(Number.NaN) : d;
    }
    return new Date();
  }

  // ── Manipulation ─────────────────────────────────────────────────────

  add(amount, unit) {
    if (!this.isValid()) return this;
    const d = new Date(this._date.getTime());
    const u = unit.toLowerCase();
    const U = GenericDateTime.Units;

    if (u.startsWith(U.DAY)) d.setDate(d.getDate() + amount);
    else if (u.startsWith(U.MONTH)) d.setMonth(d.getMonth() + amount);
    else if (u.startsWith(U.YEAR)) d.setFullYear(d.getFullYear() + amount);
    else if (u.startsWith(U.HOUR)) d.setHours(d.getHours() + amount);
    else if (u.startsWith(U.MINUTE)) d.setMinutes(d.getMinutes() + amount);
    else if (u.startsWith(U.SECOND)) d.setSeconds(d.getSeconds() + amount);
    else if (u.startsWith(U.MS)) d.setMilliseconds(d.getMilliseconds() + amount);

    this._date = d;
    return this;
  }

  subtract(amount, unit) {
    return this.add(-amount, unit);
  }

  // ── Fluid Manipulation (Option A) ────────────────────────────────────

  plusDays(n) {
    return this.add(n, 'day');
  }
  minusDays(n) {
    return this.subtract(n, 'day');
  }

  plusMonths(n) {
    return this.add(n, 'month');
  }
  minusMonths(n) {
    return this.subtract(n, 'month');
  }

  plusYears(n) {
    return this.add(n, 'year');
  }
  minusYears(n) {
    return this.subtract(n, 'year');
  }

  plusHours(n) {
    return this.add(n, 'hour');
  }
  minusHours(n) {
    return this.subtract(n, 'hour');
  }

  plusMinutes(n) {
    return this.add(n, 'minute');
  }
  minusMinutes(n) {
    return this.subtract(n, 'minute');
  }

  plusSeconds(n) {
    return this.add(n, 'second');
  }
  minusSeconds(n) {
    return this.subtract(n, 'second');
  }

  plusMs(n) {
    return this.add(n, 'ms');
  }
  minusMs(n) {
    return this.subtract(n, 'ms');
  }

  // ── Navigation ───────────────────────────────────────────────────────

  startOf(unit) {
    if (!this.isValid()) return this;
    const u = unit.toLowerCase();
    const U = GenericDateTime.Units;

    if (u.startsWith(U.YEAR)) this._date.setMonth(0, 1);
    if (u.startsWith(U.MONTH)) this._date.setDate(1);

    if (u.startsWith(U.YEAR) || u.startsWith(U.MONTH) || u.startsWith(U.DAY)) {
      this._date.setHours(0, 0, 0, 0);
    } else if (u.startsWith(U.HOUR)) {
      this._date.setMinutes(0, 0, 0);
    } else if (u.startsWith(U.MINUTE)) {
      this._date.setSeconds(0, 0);
    } else if (u.startsWith(U.SECOND)) {
      this._date.setMilliseconds(0);
    }
    return this;
  }

  endOf(unit) {
    if (!this.isValid()) return this;
    const u = unit.toLowerCase();
    const U = GenericDateTime.Units;

    if (u.startsWith(U.YEAR)) {
      this._date.setMonth(11, 31);
      this._date.setHours(23, 59, 59, 999);
    } else if (u.startsWith(U.MONTH)) {
      this._date.setMonth(this._date.getMonth() + 1, 0);
      this._date.setHours(23, 59, 59, 999);
    } else if (u.startsWith(U.DAY)) {
      this._date.setHours(23, 59, 59, 999);
    } else if (u.startsWith(U.HOUR)) {
      this._date.setMinutes(59, 59, 999);
    } else if (u.startsWith(U.MINUTE)) {
      this._date.setSeconds(59, 999);
    } else if (u.startsWith(U.SECOND)) {
      this._date.setMilliseconds(999);
    }
    return this;
  }

  // ── Comparison ───────────────────────────────────────────────────────

  isBefore(other, unit = 'ms') {
    if (!this.isValid()) return false;
    const otherDate = GenericDateTime.from(other).toDate();
    if (unit !== 'ms') {
      return (
        this.clone().startOf(unit).toDate().getTime() <
        GenericDateTime.from(other).startOf(unit).toDate().getTime()
      );
    }
    return this._date.getTime() < otherDate.getTime();
  }

  isAfter(other, unit = 'ms') {
    if (!this.isValid()) return false;
    const otherDate = GenericDateTime.from(other).toDate();
    if (unit !== 'ms') {
      return (
        this.clone().startOf(unit).toDate().getTime() >
        GenericDateTime.from(other).startOf(unit).toDate().getTime()
      );
    }
    return this._date.getTime() > otherDate.getTime();
  }

  isSame(other, unit = 'day') {
    if (!this.isValid()) return false;
    const a = this.clone().startOf(unit).toDate().getTime();
    const b = GenericDateTime.from(other).startOf(unit).toDate().getTime();
    return a === b;
  }

  // ── Formatting & Output ──────────────────────────────────────────────

  withLocale(loc) {
    this._locale = loc;
    return this;
  }

  withFormat(options) {
    this._format = options;
    return this;
  }

  _render(options = GenericDateTime.Defaults.FORMAT, locale = this._locale) {
    if (!this.isValid()) return 'Invalid Date';

    if (typeof options === 'string') {
      const opt = options.toLowerCase();
      if (opt === 'date') return new Intl.DateTimeFormat(locale, GenericDateTime.Defaults.FORMAT).format(this._date);
      if (opt === 'datetime')
        return new Intl.DateTimeFormat(locale, GenericDateTime.Defaults.DATETIME_FORMAT).format(this._date).replace(',', '');
      if (opt === 'time') return new Intl.DateTimeFormat(locale, GenericDateTime.Defaults.TIME_FORMAT).format(this._date);
      if (opt === 'iso') return this.toISOString();
      if (opt === 'full')
        return new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'long' }).format(this._date);

      return this._formatWithTokens(options, locale);
    }

    return new Intl.DateTimeFormat(locale, options).format(this._date);
  }

  _formatWithTokens(template, locale) {
    const d = this._date;
    const getLoc = (opts) => new Intl.DateTimeFormat(locale, opts).format(d);

    const map = {
      YYYY: d.getFullYear(),
      YY: String(d.getFullYear()).slice(-2),
      MMMM: getLoc({ month: 'long' }),
      MMM: getLoc({ month: 'short' }),
      MM: String(d.getMonth() + 1).padStart(2, '0'),
      M: d.getMonth() + 1,
      DD: String(d.getDate()).padStart(2, '0'),
      D: d.getDate(),
      HH: String(d.getHours()).padStart(2, '0'),
      H: d.getHours(),
      hh: String(d.getHours() % 12 || 12).padStart(2, '0'),
      h: d.getHours() % 12 || 12,
      mm: String(d.getMinutes()).padStart(2, '0'),
      m: d.getMinutes(),
      ss: String(d.getSeconds()).padStart(2, '0'),
      s: d.getSeconds(),
      A: getLoc({ hour: 'numeric', hour12: true }).slice(-2).toUpperCase(),
      a: getLoc({ hour: 'numeric', hour12: true }).slice(-2).toLowerCase(),
    };

    return template.replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g, (match) => map[match]);
  }

  asString() {
    return this._render(this._format);
  }
  asDate() {
    return this._render('date');
  }
  asDateTime() {
    return this._render('datetime');
  }
  asTime() {
    return this._render('time');
  }
  asFull() {
    return this._render('full');
  }
  asISO() {
    return this.isValid() ? this._date.toISOString() : 'Invalid Date';
  }
  asTimestamp() {
    return this._date.getTime();
  }

  // ── Standard Interop & Cloning ───────────────────────────────────────

  toDate() {
    return new Date(this._date.getTime());
  }

  clone() {
    const newObj = new GenericDateTime(this._date);
    newObj._locale = this._locale;
    newObj._format = this._format;
    return newObj;
  }

  isValid() {
    return this._date instanceof Date && !Number.isNaN(this._date.getTime());
  }

  get(unit) {
    if (!this.isValid()) return null;
    switch (unit.toLowerCase()) {
      case 'year':
        return this._date.getFullYear();
      case 'month':
        return this._date.getMonth();
      case 'date':
        return this._date.getDate();
      case 'day':
        return this._date.getDate();
      case 'weekday':
        return this._date.getDay();
      case 'hour':
        return this._date.getHours();
      case 'minute':
        return this._date.getMinutes();
      case 'second':
        return this._date.getSeconds();
      case 'ms':
        return this._date.getMilliseconds();
      default:
        return null;
    }
  }
}
